import React, {useContext, useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import "./LandingPage.css";
import overviewIcon from "../images/home.svg";
import topicsIcon from "../images/puzzlePiece.svg";
import organizingIcon from "../images/fileFolder.svg";
import domainIcon from "../images/cubeWireframe.svg";
import helpIcon from "../images/questionMarkCircle.svg";
import hamburgerMenu from "../images/hamburgerMenu.svg";
import closeMenu from "../images/closeMenu.svg";
import {NavBarEntry} from "../reusables/NavBarEntry";
import {ScreenSize} from "./utility/ScreenSize";
import {TopicDescriptionOverview} from "./pages/TopicDescriptionOverview";
import {
  Button,
  GlobalMenu,
  IconButton,
  TileApplications,
  User,
} from "@spg/component_library";
import API, {Resolution} from "../logic/API";
import {AppDomainOverview} from "./pages/AppDomainOverview";
import {Home} from "./pages/home/Home";
import {OtherSubPageMenu} from "./pages/OtherSubPageMenu";
import {KeycloakContext} from "./utility/KeycloakProvider";
import {AppSettingsContext} from "./utility/AppSettingsProvider";

/**
 * Info about a page to display on the landing page. The icon and name are listed in the sidebar with the latter also serving as the URL suffix.
 */
type Page = {
  name: string;
  icon: string;
  /** Either the element to render as the page or the name of a NavigationManagingPage, which will be rendered with the necessary prop  */
  content: JSX.Element | NavigationManagingPages;
  /** A link to open in a new tab instead of displaying a DM-page – the content won't be rendered if this is set */
  externalPage?: string;
  /** Whether the page allows for URLs with additional paths after the page-name */
  hasSubPages?: boolean;
  /** Used to prevent access to the page if the user doesn't have the necessary roles */
  authorization?: {
    /** List of roles that may view the page */
    accessibleBy: string[];
    /** A custom indicator to display instead of the page if the user isn't authorized */
    errorMessage?: JSX.Element;
  };
};

type PageName = "home" | "topics" | "other" | "domains";
type NavigationManagingPages = "topics" | "other";

type NavigationManagement = {
  isTryingToNavigate: string;
  allowNavigation: () => void;
  preventNavigation: () => void;
};

/**
 * A view with a sidebar (or topbar with hamburger menu on mobile/tablet) to select which page is displayed.
 * Accessibility features based on https://www.w3.org/WAI/ARIA/apg/patterns/menubar/examples/menubar-navigation
 * Of the listed features, 4. as well as roles and properties could be considered for further improvements.
 * The mobile view should eventually also be reviewed with a focus on accessibility.
 * Lastly, contrast ratings also have to be checked once the right design tokens are implemented.
 * Changes in the urlPaths of pages or updates to whether they have sub-pages should be reflected in the nginx config (TODO)
 */
function LandingPage() {
  const sizeCategory = useContext(ScreenSize).category;
  const intl = useIntl();
  const {keycloak} = useContext(KeycloakContext);
  const {wikiUrl, portalUrl} = useContext(AppSettingsContext);
  const location = useLocation();

  const [nextNavigationTarget, setNextNavigationTarget] = useState("");
  const allowNavigation = () => {
    if (nextNavigationTarget) navigate(nextNavigationTarget);
    setNextNavigationTarget("");
  };
  const preventNavigation = () => {
    setNextNavigationTarget("");
  };

  const pageSelection: Record<PageName, Page> = {
    home: {
      name: "Home",
      icon: overviewIcon,
      content: <Home />,
    },
    topics: {
      name: "Topics",
      icon: topicsIcon,
      content: "topics",
    },
    domains: {
      name: "Domains",
      icon: domainIcon,
      content: <AppDomainOverview />,
      hasSubPages: true,
    },
    other: {
      name: "Other",
      icon: organizingIcon,
      content: "other",
    },
  };

  const pages: Record<string, Page> = {};

  for (const pageName in pageSelection) {
    const page = pageSelection[pageName as PageName];
    if (
      (
        page.authorization?.accessibleBy || ["DM Mitglied", "DM Administration"]
      ).some((role) => keycloak.hasResourceRole(role))
    )
      pages[pageName as PageName] = page;
  }

  const helpPage = {
    name: "Help",
    icon: helpIcon,
    content: <div />,
    externalPage: wikiUrl,
  };
  const navigate = useNavigate();
  const pagePath = useLocation()
    .pathname.substring(1)
    .toLowerCase()
    .split("#")[0]
    .split("/");
  const [hoverPrimary, setHoverPrimary] = useState(false);
  const [lastHoveredPage, setLastHoveredPage] = useState<Page>(pages["home"]);
  const [navHidden, setNavHidden] = useState(true);
  const [navTransition, setNavTransition] = useState<
    "transition" | "start" | "none"
  >("none"); // "start" adds the menu to the DOM, but at the initial position before translation
  const firstRender = useRef(true);
  const [currentContent, setCurrentContent] = useState<
    JSX.Element | NavigationManagingPages | null
  >(null);
  const [keyboardNavigating, setKeyboardNavigating] = useState<
    "none" | "primary"
  >("none");
  const [userInfo, setUserInfo] = useState<User>({
    firstName: "–",
    lastName: " ",
    email: "–",
  });
  const [applications, setApplications] = useState<
    Resolution<TileApplications>
  >({
    success: false,
    unresolved: true,
  });

  /** Gives focus to the previous, current or next lastHoveredPage with wraparound */
  const focusNextNavBarIndex = (offset: -1 | 0 | 1) => {
    setKeyboardNavigating("primary");
    const navEntries = document.querySelectorAll(".navBarEntry");
    const currentIndex = Object.values(pages).findIndex(
      (p) => p.name === lastHoveredPage.name,
    );
    let nextIndex = currentIndex === -1 ? navEntries.length - 1 : currentIndex;
    nextIndex += offset + navEntries.length;
    (navEntries[nextIndex % navEntries.length] as HTMLElement).focus();
  };

  /** Returns the index of the first page in entries after currentIndex (with wraparound)
   *  that has a name starting with char – or -1 if none do */
  const getNextEntryByLetter = (
    char: string,
    currentIndex: number,
    entries: Page[],
  ) => {
    let reorderedEntries;
    if (currentIndex === entries.length - 1)
      reorderedEntries = entries.slice(0, -1);
    else
      reorderedEntries = [
        ...entries.slice(currentIndex + 1),
        ...entries.slice(0, currentIndex),
      ];
    const reorderedIndex = reorderedEntries.findIndex(
      (p) =>
        intl
          .formatMessage({
            id:
              "landingPage.nav." +
              ("icon" in p ? "" : lastHoveredPage.name.toLowerCase() + ".") +
              p.name.toLowerCase(),
          })[0]
          .toLowerCase() === char.toLowerCase(),
    );
    return reorderedIndex === -1
      ? -1
      : (reorderedIndex + currentIndex + 1) % entries.length;
  };

  const keyDownInNavBar = (keyCode: string, keyTyped: string) => {
    setKeyboardNavigating("primary");
    if (keyCode === "ArrowDown" || keyCode === "ArrowUp") {
      focusNextNavBarIndex(keyCode === "ArrowUp" ? -1 : 1);
    } else if (keyCode === "Home" || keyCode === "End") {
      const navEntries = document.querySelectorAll(".navBarEntry");
      (
        navEntries[
          keyCode === "Home" ? 0 : navEntries.length - 1
        ] as HTMLElement
      ).focus();
    } else if (keyCode === "Enter" || keyCode === "Space") {
      setKeyboardNavigating("none");
      if (lastHoveredPage.externalPage)
        window.open(lastHoveredPage.externalPage, "_blank", "noreferrer");
      else setNextNavigationTarget(lastHoveredPage.name.toLowerCase());
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement) activeElement.blur();
    } else if (keyTyped.length === 1) {
      const navEntries = document.querySelectorAll(".navBarEntry");
      const allPages: typeof pages = {...pages, Help: helpPage};
      const nextIndex = getNextEntryByLetter(
        keyTyped,
        Object.keys(allPages).findIndex(
          (p) => lastHoveredPage.name === allPages[p].name,
        ),
        Object.values(allPages),
      );
      if (nextIndex !== -1) (navEntries[nextIndex] as HTMLElement).focus();
    } else if (keyCode === "Tab") setKeyboardNavigating("none");
  };

  useEffect(() => {
    const main = document.getElementById("main");
    if (main) main.scrollTo(0, 0);
    setKeyboardNavigating("none");
    setHoverPrimary(false);
    if (
      pagePath.length === 0 ||
      (pagePath.length === 1 && pagePath[0] === "")
    ) {
      navigate("home");
      return;
    }
    if (!Object.keys(pages).some((k) => k === pagePath[0])) {
      setCurrentContent(null);
      return;
    }
    const currentPage = pages[pagePath[0] as PageName];
    if (
      currentPage.authorization &&
      !currentPage.authorization.accessibleBy.some((role) =>
        keycloak.hasResourceRole(role),
      )
    ) {
      setCurrentContent(
        currentPage.authorization.errorMessage || (
          <div className="landingPageGrid landingPageUnauthorized">
            <p>
              <FormattedMessage id="landingPage.unauthorized" />
            </p>
          </div>
        ),
      );
      return;
    }
    if (currentPage.content) {
      setCurrentContent(currentPage.content);
      return;
    }
    setCurrentContent(null);
  }, [location]);

  useEffect(() => {
    if (
      (nextNavigationTarget && !currentContent) ||
      (currentContent && typeof currentContent !== "string")
    )
      allowNavigation();
  }, [nextNavigationTarget]);

  useEffect(() => {
    if (!firstRender.current) setNavTransition("transition");
  }, [navHidden]);

  useEffect(() => {
    firstRender.current = false;
    const onBlur = () => {
      setKeyboardNavigating("none");
    };
    window.addEventListener("blur", onBlur);
    return () => {
      firstRender.current = true;
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  useEffect(() => {
    if (keycloak.idTokenParsed)
      setUserInfo({
        firstName: keycloak.idTokenParsed["given_name"],
        lastName: keycloak.idTokenParsed["family_name"],
        email: keycloak.idTokenParsed["email"],
      });
  }, [keycloak.idTokenParsed]);

  const apiCall = async () => {
    setApplications(await API.getApplications());
  };

  useEffect(() => {
    apiCall();
  }, []);

  return (
    <div className="landingPageContainer">
      <a
        id="landingPageSkipLink"
        className="sr-only"
        href={location.pathname + "#main"}
      >
        <FormattedMessage id="landingPage.link.skipToContent" />
      </a>
      <div
        className="landingPageMenu"
        onMouseLeave={() => setHoverPrimary(false)}
        onFocus={() => setHoverPrimary(true)}
        onBlur={() => {
          setHoverPrimary(false);
        }}
      >
        {sizeCategory === "Mobile" || sizeCategory === "Tablet" ? (
          <>
            <IconButton
              idBase={"landingPageMobileNav"}
              onClick={() => {
                setNavTransition("start");
                setNavHidden(!navHidden);
              }}
              buttonSize={{width: 48, height: 48}}
              iconProps={{
                href: navHidden
                  ? hamburgerMenu + "#globalG"
                  : closeMenu + "#globalG",
                title: intl.formatMessage({
                  id:
                    "landingPage.mobileMenu." + (navHidden ? "open" : "close"),
                }),
              }}
            />
            {(navTransition != "none" || !navHidden) && (
              <div
                id="landingPageMobileNavContainer"
                className={
                  "landingPageMobileNav firstLevel" +
                  (navHidden || navTransition === "start" ? " hidden" : "")
                }
                onTransitionEnd={() => {
                  setNavTransition("none");
                }}
              >
                {Object.values(pages).map((p) => {
                  return (
                    <NavBarEntry
                      key={p.name}
                      id={p.name + "NavBarEntry"}
                      page={p}
                      lastHoveredPage={{
                        get: lastHoveredPage,
                        set: setLastHoveredPage,
                      }}
                      isActive={
                        currentContent !== null &&
                        (pagePath[0] as PageName) === p.name.toLowerCase()
                      }
                      isNavHidden={navHidden}
                      hoverPrimary={hoverPrimary}
                      onMobileClick={() => {
                        setNavTransition("start");
                        setNavHidden(true);
                      }}
                      setNextNavigationTarget={setNextNavigationTarget}
                    />
                  );
                })}
                <NavBarEntry
                  key="Help"
                  id={"helpNavBarEntry"}
                  page={helpPage}
                  lastHoveredPage={{
                    get: lastHoveredPage,
                    set: setLastHoveredPage,
                  }}
                  isActive={false}
                  isNavHidden={navHidden}
                  hoverPrimary={hoverPrimary}
                  onMobileClick={() => {
                    setNavTransition("start");
                    setNavHidden(true);
                  }}
                  setNextNavigationTarget={setNextNavigationTarget}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <nav
              aria-label={intl.formatMessage({
                id: "landingPage.label.primaryNavLabel",
              })}
              className={
                keyboardNavigating === "primary" ? " keyboardNavigating" : ""
              }
            >
              <div className="landingPageSidebarPrimary">
                {Object.values(pages).map((p) => {
                  return (
                    <NavBarEntry
                      key={p.name}
                      id={p.name + "NavBarEntry"}
                      page={p}
                      lastHoveredPage={{
                        get: lastHoveredPage,
                        set: (lastHovered: Page) => {
                          setLastHoveredPage(lastHovered);
                          setHoverPrimary(true);
                        },
                      }}
                      isActive={
                        currentContent !== null &&
                        (pagePath[0] as PageName) === p.name.toLowerCase()
                      }
                      hoverPrimary={hoverPrimary}
                      onKeyDown={keyDownInNavBar}
                      setKeyboardNavigating={(primary) =>
                        setKeyboardNavigating(primary ? "primary" : "none")
                      }
                      setNextNavigationTarget={setNextNavigationTarget}
                    />
                  );
                })}
              </div>
              <div className="landingPageSidebarSecondary">
                <NavBarEntry
                  key="Help"
                  id={"helpNavBarEntry"}
                  page={helpPage}
                  lastHoveredPage={{
                    get: lastHoveredPage,
                    set: (lastHovered: Page) => {
                      setLastHoveredPage(lastHovered);
                      setHoverPrimary(true);
                    },
                  }}
                  isActive={false}
                  hoverPrimary={hoverPrimary}
                  onKeyDown={keyDownInNavBar}
                  setKeyboardNavigating={(primary) =>
                    setKeyboardNavigating(primary ? "primary" : "none")
                  }
                  setNextNavigationTarget={setNextNavigationTarget}
                />
              </div>
            </nav>
          </>
        )}
      </div>
      {sizeCategory == "Desktop" || sizeCategory == "Big Desktop" ? (
        <header>
          <div>
            <a href="/datamarket/">
              <FormattedMessage
                id="landingPage.headerTitle"
                values={{b: (chunks) => <b>{chunks}</b>}}
              />
            </a>
          </div>
        </header>
      ) : null}
      <div className="landingPageGlobalMenu">
        <GlobalMenu
          isLoggedIn
          firstName={userInfo.firstName}
          lastName={userInfo.lastName}
          email={userInfo.email}
          applications={applications.payload}
          altTextApplications={intl.formatMessage({
            id: "reusables.globalMenu.applications",
          })}
          emptyMessage={intl.formatMessage({
            id: applications.unresolved
              ? "reusables.globalMenu.applications.loading"
              : "reusables.globalMenu.applications.empty",
          })}
          ariaLabelUserImage={intl.formatMessage({
            id: "reusables.globalMenu.userWidget",
          })}
          primaryButton={
            <NavLink to={portalUrl + "/account"} tabIndex={-1}>
              <Button
                label={intl.formatMessage({
                  id: "reusables.globalMenu.userWidget.management",
                })}
                idBase={"landingPageGlobalMenuAccountManagement"}
                onClick={() => null}
                configuration={"primary"}
              />
            </NavLink>
          }
          secondaryButton={
            <NavLink
              id="landingPageGlobalMenuLogoutLink"
              to={keycloak.createLogoutUrl({
                redirectUri: window.location.origin + "/datamarket/login",
              })}
              className="landingPageGlobalMenuNavLink"
            >
              {intl.formatMessage({
                id: "reusables.globalMenu.userWidget.logout",
              })}
            </NavLink>
          }
          toggleNameTooltipMessage={intl.formatMessage({
            id: "reusables.globalMenu.userWidget.toggleName",
          })}
          toggleEmailTooltipMessage={intl.formatMessage({
            id: "reusables.globalMenu.userWidget.toggleMail",
          })}
        />
      </div>
      <main id="main" className="landingPageContentContainer">
        <div className="landingPageContent">
          {currentContent && typeof currentContent == "string"
            ? {
                other: (
                  <OtherSubPageMenu
                    navigationManagement={{
                      isTryingToNavigate: nextNavigationTarget,
                      allowNavigation,
                      preventNavigation,
                    }}
                  />
                ),
                topics: (
                  <TopicDescriptionOverview
                    navigationManagement={{
                      isTryingToNavigate: nextNavigationTarget,
                      allowNavigation,
                      preventNavigation,
                    }}
                  />
                ),
              }[currentContent]
            : currentContent || (
                <div className="landingPageGrid landingPageError">
                  <p>
                    <FormattedMessage id="landingPage.notFound" />
                  </p>
                </div>
              )}
        </div>
      </main>
    </div>
  );
}

export {LandingPage, type Page, type NavigationManagement};
